/* eslint-disable @typescript-eslint/no-explicit-any */
import NextImage from 'next/legacy/image'
import { memo, useCallback, useMemo, type ReactNode } from 'react'
import Select from 'react-select'
import WindowedSelect from 'react-windowed-select'
import { useTranslate } from '../../contexts/TranslationContext'

type UseState<Value> = readonly [Value, (value: Value) => void]

export const ComboboxInput = memo(function ComboboxInput(props: {
	searchPlaceholder?: string
	items: { id: string; label: string; imageUrl?: string }[]
	state: UseState<string | null>
	windowed?: boolean
	height?: number
	isClearable?: boolean
	noOptionsMessage?: ReactNode
	hasEmptyOption?: boolean
	isNotSearchable?: boolean
	hasPreview?: boolean
	name?: string
}) {
	const options = useMemo(
		() =>
			props.items.map((item) => ({
				value: item.id,
				label: item.label,
				imageUrl: item.imageUrl ?? null,
			})),
		[props.items],
	)

	const translate = useTranslate()

	const optionsWithEmptyOption = [
		{ value: 'undisclosed', label: translate('select.dontKnow'), imageUrl: null },
		...options,
	]

	const value = useMemo(() => options.find((item) => item.value === props.state[0]), [options, props.state])

	const handleChange = useCallback(
		(value: unknown) => {
			if (!value) {
				value = ''
			}
			props.state[1]((value as { value: string }).value)
		},
		[props.state],
	)

	const boxShadow = 'none'
	const height = props.height ?? 38

	const primary25 = 'rgb(168,168,168, 0.5)' // $greyMediumLight
	const primary = '100D3D' //$primary
	const primary50 = '#A8A8A8' // $greyMedium

	const nullValue = props.hasEmptyOption ? undefined : ''

	if (props.windowed) {
		return (
			<WindowedSelect
				windowThreshold={100}
				name={props.name}
				value={value ?? nullValue}
				aria-label={props.name}
				aria-labelledby={props.name}
				onChange={handleChange}
				isSearchable={!props.isNotSearchable}
				options={props.hasEmptyOption ? optionsWithEmptyOption : options}
				placeholder={props.searchPlaceholder}
				escapeClearsValue={true}
				formatOptionLabel={
					props.hasPreview
						? (item: any) => (
								<div style={{ display: 'flex', alignItems: 'center' }}>
									<div style={{ display: 'inline-block', marginRight: '8px' }}>
										<NextImage
											src={item.imageUrl ?? '/images/placeholder-product.png'}
											width={36}
											height={36}
											title={translate('placeholder.image.alt')}
											alt={translate('placeholder.image.alt')}
											layout="intrinsic"
										/>
									</div>
									{item.label}
								</div>
							)
						: undefined
				}
				styles={{
					placeholder: (defaultStyles) => ({
						...defaultStyles,
						color: '#555257', // greyHeavy
					}),
					control: (base) => ({
						...base,
						boxShadow,
						height,
						transition: '0.2s ease-in all',
						borderColor: '#827D85', // greyDark
						'&:hover': {
							borderColor: '#100D3D', // primary
							boxShadow: '0 4px 5px 0 rgba(0, 0, 0, 0.1)',
						},
					}),
					singleValue: (provided) => ({
						...provided,
						color: '#100D3D',
						fontWeight: 700,
					}),
					option: (provided) => ({
						...provided,
						color: '#100D3D',
					}),
				}}
				isClearable={props.isClearable}
				noOptionsMessage={() => props.noOptionsMessage}
				components={{
					IndicatorSeparator: () => null,
				}}
				theme={(theme) => ({
					...theme,
					colors: {
						...theme.colors,
						primary25,
						primary,
						primary50,
					},
				})}
			/>
		)
	}

	return (
		<>
			<Select
				value={value ?? nullValue}
				inputId={props.name}
				onChange={handleChange}
				name={props.name}
				aria-label={props.name}
				options={props.hasEmptyOption ? optionsWithEmptyOption : options}
				formatOptionLabel={
					props.hasPreview
						? (item: any) => (
								<div style={{ display: 'flex', alignItems: 'center' }}>
									<div style={{ display: 'inline-block', marginRight: '8px' }}>
										<NextImage
											src={item.imageUrl ?? '/images/placeholder-product.png'}
											width={36}
											height={36}
											alt=""
											layout="intrinsic"
										/>
									</div>
									{item.label}
								</div>
							)
						: undefined
				}
				isClearable={props.isClearable}
				placeholder={props.searchPlaceholder}
				isSearchable={!props.isNotSearchable}
				escapeClearsValue={true}
				styles={{
					placeholder: (defaultStyles) => ({
						...defaultStyles,
						color: '#555257', // greyHeavy
					}),
					control: (base) => ({
						...base,
						boxShadow,
						height,
						transition: '0.2s ease-in all',
						borderColor: '#827D85', // greyDark
						'&:hover': {
							borderColor: '#100D3D',
							boxShadow: '0 4px 5px 0 rgba(0, 0, 0, 0.1)',
						},
					}),
					singleValue: (provided) => ({
						...provided,
						color: '#100D3D',
						fontWeight: 700,
					}),
					option: (provided) => ({
						...provided,
						color: '#100D3D',
					}),
				}}
				noOptionsMessage={() => props.noOptionsMessage}
				components={{
					IndicatorSeparator: () => null,
				}}
				theme={(theme) => ({
					...theme,
					colors: {
						...theme.colors,
						primary25,
						primary,
						primary50,
					},
				})}
			/>
		</>
	)
})
